import {SSEHandlers} from "./types";

export class SSEManager {
  private es: EventSource | null = null;
  private boundSessionId: string | null = null;
  private lastHeartbeatTime: number = Date.now();
  private heartbeatTimeout: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_TIMEOUT_MS = 90000; // 90 seconds - if no heartbeat for this long, consider connection dead

  public isConnectedTo(sessionId: string): boolean {
    return !!this.es && this.es.readyState === EventSource.OPEN && this.boundSessionId === String(sessionId);
  }

  public get current(): EventSource | null {
    return this.es;
  }

  public close(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
    try { this.es?.close(); } catch {}
    this.es = null;
    this.boundSessionId = null;
    this.lastHeartbeatTime = Date.now();
  }

  private resetHeartbeatTimeout(handlers: SSEHandlers): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }
    
    this.heartbeatTimeout = setTimeout(() => {
      // Connection appears dead - trigger error handler for reconnection
      if (this.es && this.es.readyState !== EventSource.CLOSED) {
        handlers.onError?.(new Error('Heartbeat timeout - connection appears dead'));
      }
    }, this.HEARTBEAT_TIMEOUT_MS);
  }

  public async connect(streamUrl: string, sessionId: string, handlers: SSEHandlers): Promise<EventSource> {
    if (this.es && this.boundSessionId !== String(sessionId)) {
      this.close();
    }
    if (this.es && this.boundSessionId === String(sessionId)) {
      if (this.es.readyState === EventSource.OPEN || this.es.readyState === EventSource.CONNECTING) {
        return this.es;
      }
    }

    const es = new EventSource(streamUrl);
    this.es = es;
    this.boundSessionId = String(sessionId);
    this.lastHeartbeatTime = Date.now();

    es.onopen = () => { 
      this.lastHeartbeatTime = Date.now();
      this.resetHeartbeatTimeout(handlers);
      handlers.onOpen?.(); 
    };
    
    es.onmessage = (event) => {
      try { 
        const data = JSON.parse(event.data);
        // Handle heartbeat messages - reset timeout
        if (data.type === 'heartbeat') {
          this.lastHeartbeatTime = Date.now();
          this.resetHeartbeatTimeout(handlers);
        }
        handlers.onMessage(data); 
      } catch {}
    };
    
    es.onerror = (err) => { 
      handlers.onError?.(err); 
    };

    // Set initial heartbeat timeout
    this.resetHeartbeatTimeout(handlers);

    return es;
  }

  public getLastHeartbeatTime(): number {
    return this.lastHeartbeatTime;
  }

  public isConnectionHealthy(): boolean {
    if (!this.es || this.es.readyState !== EventSource.OPEN) {
      return false;
    }
    const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeatTime;
    return timeSinceLastHeartbeat < this.HEARTBEAT_TIMEOUT_MS;
  }
}